import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { settingsApi, type ThemePatch, type ThemePreview, type ThemeSettings } from "@/services/settings";
import { useCan } from "@/auth/permissions";
import { PageShell, LoadingState, ErrorState } from "@/shared/ui/PageHeader";
import { tokensToStyle } from "@/theme/applyTheme";
import { useRefreshTheme, useThemeSettings } from "@/theme/useTheme";
import { SettingsTabs } from "./SettingsTabs";

/**
 * THEME-1 · 系统设置 →「主题」：预设二选一（经典 / 西部松绿）+ 主色 / 强调色（可改，带对比度提示）+ 后台版式 + 同行站外观 + 实时预览块。
 * 样稿：designs/mockups/theme/个人站主题A-完整样稿.html（tag theme-a-mockup-v1）。
 *
 * 🔴 令牌派生（hover / soft / 色阶 / 对比度检查）只在后端：预览走 POST /api/v1/settings/theme:preview（只算不存），保存走 PATCH（对比度不够 422 + 提示）。
 * logo 和产品名不在这里另存一份——继续用「通用设置」里的品牌设置。
 */
const PRESETS: { key: "classic" | "pine"; name: string; desc: string; swatch: string[] }[] = [
  { key: "classic", name: "经典蓝", desc: "现在的样子（默认）", swatch: ["#2563EB", "#DBEAFE", "#0F172A"] },
  { key: "pine", name: "西部松绿", desc: "主题 A · 松绿 + 砂岩橙", swatch: ["#1F5E4A", "#C8763A", "#FAFAF7"] },
];

interface Draft {
  preset: "classic" | "pine";
  brand: string | null;     // null = 用预设自带
  accent: string | null;
  admin_layout: "sidebar" | "topnav";
  partner_layout: "classic" | "bms";
}

function toDraft(t: ThemeSettings): Draft {
  return { preset: t.preset, brand: t.brand_custom ? t.brand : null, accent: t.accent_custom ? t.accent : null, admin_layout: t.admin_layout, partner_layout: t.partner_layout };
}

export function ThemeSettingsPage() {
  const canEdit = useCan("system.settings.update");
  const q = useThemeSettings();
  const refresh = useRefreshTheme();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [preview, setPreview] = useState<ThemePreview | null>(null);
  const [saved, setSaved] = useState(false);
  const seq = useRef(0);

  useEffect(() => { if (q.data && !draft) setDraft(toDraft(q.data)); }, [q.data, draft]);

  // 实时预览：改了就让后端算一遍（防抖 300ms，只认最后一次的结果）
  useEffect(() => {
    if (!draft) return;
    const my = ++seq.current;
    const t = setTimeout(() => {
      settingsApi.previewTheme({ preset: draft.preset, brand: draft.brand, accent: draft.accent })
        .then((r) => { if (my === seq.current) setPreview(r); })
        .catch(() => { if (my === seq.current) setPreview(null); });
    }, 300);
    return () => clearTimeout(t);
  }, [draft?.preset, draft?.brand, draft?.accent]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: (p: ThemePatch) => settingsApi.patchTheme(p),
    onSuccess: (next) => { refresh(next); setDraft(toDraft(next)); setSaved(true); },
  });

  if (q.isError) return <ErrorState message="主题设置加载失败" onRetry={() => q.refetch()} />;
  if (!q.data || !draft) return <LoadingState />;
  const base = q.data;
  const defaults = preview ? undefined : base.defaults;   // 仅在预览没回来时兜底
  const presetColors = (p: "classic" | "pine") => (p === base.preset ? base.defaults : { brand: p === "pine" ? "#1F5E4A" : "#2563EB", accent: "#C8763A" });
  const effBrand = draft.brand ?? presetColors(draft.preset).brand ?? defaults?.brand ?? "#2563EB";
  const effAccent = draft.accent ?? presetColors(draft.preset).accent ?? defaults?.accent ?? "#C8763A";
  const dirty = JSON.stringify(draft) !== JSON.stringify(toDraft(base));
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => { setSaved(false); setDraft({ ...draft, [k]: v }); };

  const submit = () => {
    const patch: ThemePatch = {};
    if (draft.preset !== base.preset) patch.preset = draft.preset;
    if (draft.admin_layout !== base.admin_layout) patch.admin_layout = draft.admin_layout;
    if (draft.partner_layout !== base.partner_layout) patch.partner_layout = draft.partner_layout;
    const baseBrand = base.brand_custom ? base.brand : null;
    const baseAccent = base.accent_custom ? base.accent : null;
    if (draft.brand !== baseBrand) patch.brand = draft.brand;
    if (draft.accent !== baseAccent) patch.accent = draft.accent;
    save.mutate(patch);
  };

  const err = (save.error as Error | null)?.message ?? null;
  const style = tokensToStyle(preview?.tokens ?? base.tokens) as React.CSSProperties;

  return (
    <PageShell title="主题" subtitle="换预设 / 主色 / 强调色 / 后台版式；logo 和产品名在「通用设置」里改">
      <SettingsTabs />

      <section className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="text-[14px] font-bold text-slate-900">预设</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              disabled={!canEdit}
              aria-pressed={draft.preset === p.key}
              data-testid={`preset-${p.key}`}
              onClick={() => set("preset", p.key)}
              className={`flex items-center gap-3 rounded-lg border p-3 text-left ${draft.preset === p.key ? "border-blue-600 ring-1 ring-blue-600" : "border-slate-200 hover:border-slate-300"} disabled:opacity-60`}
            >
              <span className="flex gap-1">{p.swatch.map((c) => <i key={c} className="h-6 w-6 rounded-md border border-slate-200" style={{ background: c }} />)}</span>
              <span><b className="block text-[13px] text-slate-900">{p.name}</b><span className="text-[12px] text-slate-500">{p.desc}</span></span>
            </button>
          ))}
        </div>
      </section>

      <section className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="text-[14px] font-bold text-slate-900">颜色</h2>
        {([["brand", "主色（按钮 / 选中 / 链接）", effBrand, draft.brand, preview?.contrast.brand, "白字需 ≥ 4.5:1"], ["accent", "强调色（价格 / 最要紧的一个动作）", effAccent, draft.accent, preview?.contrast.accent, "白字需 ≥ 3:1"]] as const).map(([key, label, eff, custom, ratio, need]) => (
          <div key={key} className="flex flex-wrap items-center gap-3">
            <span className="w-60 text-[13px] text-slate-700">{label}</span>
            <input type="color" aria-label={`${label} 取色`} disabled={!canEdit} value={eff} onChange={(e) => set(key, e.target.value.toUpperCase())} className="h-8 w-10 cursor-pointer rounded border border-slate-300 bg-white p-0.5" />
            <input
              aria-label={`${key === "brand" ? "主色" : "强调色"}色值`}
              disabled={!canEdit}
              value={custom ?? eff}
              onChange={(e) => set(key, e.target.value.trim() === "" ? null : e.target.value)}
              className="w-28 rounded-md border border-slate-300 px-2 py-1.5 font-mono text-[12.5px] uppercase focus:border-blue-500 focus:outline-none"
            />
            <span className="text-[12px] text-slate-500">与白字对比度 <b data-testid={`contrast-${key}`}>{ratio ?? "—"}</b>:1（{need}）</span>
            {custom !== null && canEdit && <button type="button" onClick={() => set(key, null)} className="text-[12px] text-blue-600 hover:underline">恢复默认</button>}
          </div>
        ))}
        {preview?.error && <p role="alert" data-testid="theme-error" className="rounded-md bg-rose-50 px-3 py-2 text-[13px] font-medium text-rose-700">{preview.error}</p>}
        <p className="text-[12px] text-slate-500">hover / 浅底 / 色阶由系统按这两个色自动算（预览里就是算好的结果）。成功色固定用独立的绿色，不会和品牌绿混。</p>
      </section>

      <section className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="text-[14px] font-bold text-slate-900">版式</h2>
        <div className="flex flex-wrap items-center gap-3">
          <span className="w-40 text-[13px] text-slate-700">后台版式</span>
          {([["sidebar", "左侧栏（现在）"], ["topnav", "顶部模块导航"]] as const).map(([k, l]) => (
            <label key={k} className="flex items-center gap-1.5 text-[13px]"><input type="radio" className="accent-blue-600" name="admin_layout" disabled={!canEdit} checked={draft.admin_layout === k} onChange={() => set("admin_layout", k)} /> {l}</label>
          ))}
          <span className="text-[12px] text-slate-500">顶部导航：一级模块在顶栏、二级在左侧浅色细栏，Ctrl K 直达</span>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="w-40 text-[13px] text-slate-700">同行站外观</span>
          {([["classic", "经典（现在）"], ["bms", "照参考站（深蓝侧栏 + 多标签页）"]] as const).map(([k, l]) => (
            <label key={k} className="flex items-center gap-1.5 text-[13px]"><input type="radio" className="accent-blue-600" name="partner_layout" disabled={!canEdit} checked={draft.partner_layout === k} onChange={() => set("partner_layout", k)} /> {l}</label>
          ))}
        </div>
        <p className="text-[12px] text-slate-500">logo 和产品名：<Link to="/admin/settings" className="text-blue-600 hover:underline">在「通用设置」里改 →</Link></p>
      </section>

      <section className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-5" aria-label="实时预览">
        <h2 className="text-[14px] font-bold text-slate-900">预览</h2>
        <div style={style} className="flex flex-col gap-3 rounded-lg border border-slate-200 p-4" data-testid="theme-preview">
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" className="rounded-md bg-blue-600 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-blue-700">＋ 新建订单</button>
            <button type="button" className="rounded-md bg-accent px-3 py-1.5 text-[13px] font-medium text-white hover:bg-accent-hover">处理异常 1</button>
            <button type="button" className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-[13px] text-slate-700">导出</button>
            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11.5px] font-medium text-emerald-700">已确认</span>
            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11.5px] font-medium text-amber-700">待确认</span>
            <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[11.5px] font-medium text-rose-700">异常</span>
            <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11.5px] font-medium text-blue-700">同行</span>
            <span className="rounded-full bg-blue-600 px-3 py-0.5 text-[12px] text-white">全部</span>
            <span className="rounded-full border border-slate-300 px-3 py-0.5 text-[12px] text-slate-600">西雅图</span>
          </div>
          <table className="w-full text-[13px]">
            <thead><tr className="bg-slate-50 text-left text-slate-400"><th className="px-2 py-1.5 font-medium">订单号</th><th className="px-2 py-1.5 font-medium">产品</th><th className="px-2 py-1.5 text-right font-medium">金额</th><th className="px-2 py-1.5 font-medium">状态</th></tr></thead>
            <tbody>
              <tr className="border-t border-slate-100 bg-blue-50"><td className="px-2 py-1.5 text-blue-700">ORD202610000031</td><td className="px-2 py-1.5">SF-AP-6A 旧金山 4 日游</td><td className="px-2 py-1.5 text-right tabular-nums">$1,608.00</td><td className="px-2 py-1.5">待确认</td></tr>
              <tr className="border-t border-slate-100"><td className="px-2 py-1.5 text-blue-700">ORD202610000030</td><td className="px-2 py-1.5">LAS-AP-4A 拉斯 4 日游</td><td className="px-2 py-1.5 text-right tabular-nums">$1,592.00</td><td className="px-2 py-1.5">已确认</td></tr>
            </tbody>
          </table>
          <div className="flex gap-2 text-center text-[12px] font-semibold">
            <span className="w-16 rounded bg-emerald-50 py-1 text-emerald-700">$398<small className="block text-[10px] font-normal">充足</small></span>
            <span className="w-16 rounded bg-amber-50 py-1 text-amber-700">$398<small className="block text-[10px] font-normal">紧张</small></span>
            <span className="w-16 rounded bg-slate-100 py-1 text-slate-400">$398<small className="block text-[10px] font-normal">不够</small></span>
            <span className="w-16 rounded bg-rose-50 py-1 text-rose-700">$398<small className="block text-[10px] font-normal">满</small></span>
          </div>
          <p className="text-[12px] text-slate-500">砂岩橙只用在「最要紧的那一个动作」和价格上，一屏最多一处；成功状态用独立的绿色。</p>
        </div>
      </section>

      <div className="flex items-center justify-end gap-3">
        {err && <span role="alert" className="text-[13px] font-medium text-rose-600">{err}</span>}
        {saved && !dirty && <span className="text-[13px] text-emerald-600">已保存，三端按新主题生效</span>}
        {canEdit ? (
          <button
            type="button"
            onClick={submit}
            disabled={!dirty || save.isPending || !!preview?.error}
            className="rounded-md bg-blue-600 px-4 py-2 text-[13px] font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {save.isPending ? "保存中…" : "保存主题"}
          </button>
        ) : (
          <span className="text-[12px] text-slate-400">只有老板 / 技术管理员能改主题</span>
        )}
      </div>
    </PageShell>
  );
}
